import type React from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { AppSidebar } from '@/components/app-sidebar';
import { PageTransition } from '@/components/page-transition';
import { SidebarInset, SidebarProvider, SidebarTrigger } from '@/components/ui/sidebar';
import { CompanyLogoProvider } from '@/contexts/company-logo-context';

// `not-sr-only` resets position and padding. The important utilities win so the
// link stays fixed at the top of the viewport, with padding, once it is focused.
const SKIP_LINK_CLASS =
  'sr-only focus:not-sr-only focus:fixed! focus:top-2 focus:left-2 focus:z-50 focus:rounded-md focus:bg-background focus:px-4! focus:py-2! focus:text-sm focus:font-medium focus:text-foreground focus:shadow-md focus:ring-2 focus:ring-ring focus:outline-none';

function skipToMain(event: React.MouseEvent<HTMLAnchorElement>): void {
  const main = document.getElementById('main-content');
  if (!main) return;
  // A fragment navigation would reset React Router's location.state.
  event.preventDefault();
  // Focusing <main> itself makes NVDA read out the whole page (see RouteAnnouncer).
  const heading = main.querySelector<HTMLElement>('h1');
  const target = heading?.textContent?.trim() ? heading : main;
  // Focusable only while in use, so clicks on the page don't move focus.
  target.setAttribute('tabindex', '-1');
  target.focus();
  target.addEventListener('blur', () => target.removeAttribute('tabindex'), { once: true });
}

export const AppLayout: React.FC = () => {
  const location = useLocation();
  return (
    <CompanyLogoProvider>
      <SidebarProvider
        style={
          {
            '--sidebar-width': 'calc(var(--spacing) * 60)',
          } as React.CSSProperties
        }
      >
        {/* biome-ignore lint/a11y/useValidAnchor: skip link stays an anchor so it is the first tab stop; the click handler blocks the fragment navigation that clears location.state */}
        <a href="#main-content" onClick={skipToMain} className={SKIP_LINK_CLASS}>
          Skip to main content
        </a>
        <AppSidebar variant="inset" />
        {/* Header stays outside <main> so it is a banner landmark, not just a nested header. */}
        <div className="flex w-full flex-1 flex-col bg-card">
          <header className="bg-muted/50">
            {/* Mobile-only control to open the navigation drawer; the sidebar is always visible on desktop. */}
            <SidebarTrigger className="m-2 md:hidden" />
          </header>
          <SidebarInset id="main-content" className="border-t-0 bg-muted/50 outline-none">
            <div className="@container/main flex flex-1 flex-col gap-2">
              <PageTransition key={location.pathname}>
                <Outlet />
              </PageTransition>
            </div>
          </SidebarInset>
        </div>
      </SidebarProvider>
    </CompanyLogoProvider>
  );
};
